import { getSubscriptionType } from "utils/utils";

export const ZENDESK_WIDGET_KEY = process.env.REACT_APP_ZENDESK_WIDGET_KEY || "";

export const ZENDESK_SCRIPT_ID = "ze-snippet";

export const ZENDESK_CONVERSATION_FIELDS = {
	USER_EMAIL: "48443710310801",
	USER_ROLE: "48443725204753",
	PROPERTY_ID: "47625038878225",
	PROPERTY_NAME: "47625004526609",
	GROUP_ID: "47625019026577",
	GROUP_NAME: "47625047734289",
};

const BASE_TAGS = [
	"haddock_app",
	"haddock_web_app",
	"zendesk_widget",
	"haddock_hr",
];

let scriptLoadPromise = null;

export function normalizeZendeskTagValue(value) {
	if (value == null || value === "") return "";

	return String(value)
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");
}

export function getZendeskUserDisplayName(user) {
	if (!user) return "";
	if (user.name) return user.name.trim();
	return `${user.first_name || ""} ${user.last_name || ""}`.trim();
}

export function getCompanyFromUser(user) {
	if (!user) return null;
	if (user.company) return user.company;
	if (user.employee_with_contract?.company) {
		return user.employee_with_contract.company;
	}
	return null;
}

/** Rol efectivo en la company activa: admin | supervisor | employee */
export function getZendeskEffectiveRole(user) {
	const roles = Array.isArray(user?.roles) ? user.roles : [];
	if (roles.includes("ADMIN")) return "admin";
	if (roles.includes("SUPERVISOR")) return "supervisor";
	return "employee";
}

/** region.id de la property activa (p. ej. ES, ES-CN, PT, MX). */
export function getCompanyRegionId(company) {
	if (!company) return null;
	if (company.region?.id != null && company.region.id !== "") {
		return company.region.id;
	}
	if (company.region_id != null && company.region_id !== "") {
		return company.region_id;
	}
	if (typeof company.region === "string" && company.region !== "") {
		return company.region;
	}
	return null;
}

/** Tipo interno de property (regular, premium, KA). Solo si viene explícito del backend. */
export function getPropertyType(company) {
	const propertyType =
		company?.property_type ?? company?.propertyType ?? company?.type ?? null;
	if (propertyType == null || propertyType === "") return null;
	return propertyType;
}

/**
 * Holding / grupo empresarial padre de la property.
 * No confundir con groups_supervised ni venues de planificación.
 */
export function getHoldingGroup(company) {
	const holding =
		company?.holding ??
		company?.business_group ??
		company?.parent_group ??
		company?.corporate_group ??
		null;

	if (!holding) return null;

	const id = holding.id ?? holding.group_id ?? null;
	const name = holding.name ?? holding.group_name ?? null;

	if (id == null && !name) return null;
	return { id, name };
}

export function isExplicitTrialAccount(user) {
	const subscription = user?.subscription;
	if (!subscription) return false;

	return (
		subscription.is_trial === true ||
		subscription.free_trial === true ||
		subscription.trial === true
	);
}

export function isSubscriptionBlocked(user, company) {
	if (company?.subscription_blocked === true) return true;
	if (user?.subscription?.subscription_blocked === true) return true;
	if (company?.subscription?.blocked === true) return true;
	return false;
}

export function buildZendeskConversationTags(user) {
	const tags = [...BASE_TAGS];
	const company = getCompanyFromUser(user);

	const regionId = getCompanyRegionId(company);
	const normalizedRegion = normalizeZendeskTagValue(regionId);
	if (normalizedRegion) {
		tags.push(`region_${normalizedRegion}`);
	}

	const propertyType = getPropertyType(company);
	const normalizedPropertyType = normalizeZendeskTagValue(propertyType);
	if (normalizedPropertyType) {
		tags.push(`property_type_${normalizedPropertyType}`);
	}

	const role = getZendeskEffectiveRole(user);
	if (role) {
		tags.push(`role_${role}`);
	}

	const plan = getSubscriptionType();
	const normalizedPlan = normalizeZendeskTagValue(plan);
	if (normalizedPlan) {
		tags.push(`plan_${normalizedPlan}`);
	}

	if (isSubscriptionBlocked(user, company)) {
		tags.push("subscription_blocked");
	}

	if (isExplicitTrialAccount(user)) {
		tags.push("trial_account");
	}

	return tags;
}

export function buildZendeskConversationFields(user) {
	if (!user) return [];

	const company = getCompanyFromUser(user);
	const holdingGroup = getHoldingGroup(company);
	const fields = [];

	if (user.email) {
		fields.push({
			id: ZENDESK_CONVERSATION_FIELDS.USER_EMAIL,
			value: user.email,
		});
	}

	const userRole = getZendeskEffectiveRole(user);
	if (userRole) {
		fields.push({
			id: ZENDESK_CONVERSATION_FIELDS.USER_ROLE,
			value: userRole,
		});
	}

	if (company?.code) {
		fields.push({
			id: ZENDESK_CONVERSATION_FIELDS.PROPERTY_ID,
			value: String(company.code),
		});
	}

	if (company?.name) {
		fields.push({
			id: ZENDESK_CONVERSATION_FIELDS.PROPERTY_NAME,
			value: company.name,
		});
	}

	if (holdingGroup?.id != null) {
		fields.push({
			id: ZENDESK_CONVERSATION_FIELDS.GROUP_ID,
			value: String(holdingGroup.id),
		});
	}

	if (holdingGroup?.name) {
		fields.push({
			id: ZENDESK_CONVERSATION_FIELDS.GROUP_NAME,
			value: holdingGroup.name,
		});
	}

	return fields;
}

export function buildZendeskContextSignature(user) {
	const company = getCompanyFromUser(user);
	const holdingGroup = getHoldingGroup(company);

	return [
		user?.id ?? "",
		company?.code ?? "",
		company?.name ?? "",
		holdingGroup?.id ?? "",
		holdingGroup?.name ?? "",
		getZendeskEffectiveRole(user),
		getSubscriptionType() ?? "",
		getCompanyRegionId(company) ?? "",
		getPropertyType(company) ?? "",
		isSubscriptionBlocked(user, company) ? "blocked" : "",
		isExplicitTrialAccount(user) ? "trial" : "",
	].join("|");
}

function waitForZendeskApi(timeoutMs = 15000) {
	return new Promise((resolve, reject) => {
		const startedAt = Date.now();

		const check = () => {
			if (typeof window.zE === "function") {
				resolve(window.zE);
				return;
			}

			if (Date.now() - startedAt > timeoutMs) {
				reject(new Error("Zendesk Messaging API (window.zE) no disponible"));
				return;
			}

			window.setTimeout(check, 100);
		};

		check();
	});
}

export function loadZendeskMessagingScript() {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Zendesk solo está disponible en el navegador"));
	}

	if (!ZENDESK_WIDGET_KEY) {
		return Promise.reject(
			new Error(
				"REACT_APP_ZENDESK_WIDGET_KEY no está configurada (ver .env.haddock)"
			)
		);
	}

	if (typeof window.zE === "function") {
		return Promise.resolve(window.zE);
	}

	if (scriptLoadPromise) {
		return scriptLoadPromise;
	}

	scriptLoadPromise = new Promise((resolve, reject) => {
		const existingScript = document.getElementById(ZENDESK_SCRIPT_ID);

		const onReady = () => {
			waitForZendeskApi()
				.then(resolve)
				.catch((error) => {
					scriptLoadPromise = null;
					reject(error);
				});
		};

		if (existingScript) {
			onReady();
			return;
		}

		const script = document.createElement("script");
		script.id = ZENDESK_SCRIPT_ID;
		script.async = true;
		script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_WIDGET_KEY}`;
		script.onload = onReady;
		script.onerror = () => {
			scriptLoadPromise = null;
			reject(new Error("No se pudo cargar el snippet de Zendesk Messaging"));
		};

		document.body.appendChild(script);
	});

	return scriptLoadPromise;
}

export function logoutZendeskMessenger() {
	if (typeof window.zE !== "function") return;

	try {
		window.zE("messenger", "logoutUser");
	} catch (error) {
		console.error("Error al cerrar sesión de Zendesk Messaging:", error);
	}
}

export function hideZendeskMessenger() {
	if (typeof window.zE !== "function") return;

	try {
		window.zE("messenger", "hide");
	} catch (error) {
		console.error("Error al ocultar Zendesk Messaging:", error);
	}
}

export function showZendeskMessenger() {
	if (typeof window.zE !== "function") return;

	try {
		window.zE("messenger", "show");
	} catch (error) {
		console.error("Error al mostrar Zendesk Messaging:", error);
	}
}

export function closeZendeskMessenger() {
	if (typeof window.zE !== "function") return;

	try {
		window.zE("messenger", "close");
	} catch (error) {
		console.error("Error al cerrar Zendesk Messaging:", error);
	}
}

export function openZendeskMessenger() {
	if (typeof window.zE !== "function") return;

	try {
		window.zE("messenger", "open");
	} catch (error) {
		console.error("Error al abrir Zendesk Messaging:", error);
	}
}

/**
 * Desmonta y vuelve a cargar el snippet de Zendesk Messaging.
 * Necesario tras un loginUser fallido: logoutUser NO recupera el spinner
 * de autenticación y el messenger se queda en blanco indefinidamente.
 */
export function remountZendeskMessenger() {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Zendesk solo está disponible en el navegador"));
	}

	try {
		if (typeof window.zE === "function") {
			window.zE("messenger", "close");
			window.zE("messenger", "hide");
			window.zE("messenger", "logoutUser");
		}
	} catch {
		// ignore: el messenger puede estar a medias tras un login fallido
	}

	document
		.querySelectorAll('script[src*="zdassets.com"], script#ze-snippet')
		.forEach((el) => el.remove());

	document.querySelectorAll("iframe").forEach((iframe) => {
		const title = iframe.getAttribute("title") || "";
		const isMessengerFrame =
			iframe.id === "launcher" ||
			/mensajer|messaging|messenger|zendesk/i.test(title);

		if (!isMessengerFrame) return;

		const parent = iframe.parentElement;
		if (parent && parent !== document.body && parent.childElementCount <= 2) {
			parent.remove();
		} else {
			iframe.remove();
		}
	});

	[
		"zE",
		"zEmbed",
		"zEACLoaded",
		"zESettings",
		"zEQueue",
		"zEWidgets",
	].forEach((key) => {
		try {
			delete window[key];
		} catch {
			window[key] = undefined;
		}
	});

	scriptLoadPromise = null;

	return loadZendeskMessagingScript();
}

export function applyZendeskMessengerContext(user) {
	if (typeof window.zE !== "function" || !user) return;

	const tags = buildZendeskConversationTags(user);
	const fields = buildZendeskConversationFields(user);

	try {
		window.zE("messenger:set", "conversationTags", tags);
		window.zE("messenger:set", "conversationFields", fields);
	} catch (error) {
		console.error("Error al configurar el contexto de Zendesk Messaging:", error);
	}
}
